import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { CustomAlert } from 'components/AlertsCollection';
import { ConfirmationDialogLabels } from 'components/ConfirmationDialogBox';
import { ListedStudy } from 'helper/studies';
import baseApi, { GenericResponse, ResponseError } from 'services/api/base';
import { CancelStudyResponse } from 'services/api/studies';
import useConfirmationDialogBox from 'utils/useConfirmationDialogBox';

// O worker Go grava o Cancelado logo depois do kill; um segundo refetch pega o status final
export const REFETCH_AFTER_CANCEL_MS = 3000;

// Tag da lista que a ação invalida: 'Study' = Flux Upload, 'PersonalizedStudy' = Flux Personalizado
export type StudyListTag = 'Study' | 'PersonalizedStudy';

type Trigger<Result> = (id: number) => { unwrap: () => Promise<Result> };

/** Só o que as ações leem do estudo: a tela nova passa uma referência mínima (#1994). */
export type StudyActionTarget = Pick<ListedStudy, 'id' | 'ds_nome_estudo'>;

export type StudyActionResult = 'delete' | 'cancel';

export interface StudyActionsMutations {
  triggerDelete: Trigger<GenericResponse<null>>;
  deleting: boolean;
  triggerCancel: Trigger<CancelStudyResponse>;
  cancelling: boolean;
  tag: StudyListTag;
}

/** Opções da tela nova (Meus Estudos); sem elas, nada muda no Upload nem na Row antiga. */
export interface StudyActionsOptions {
  /** Título e botões dos diálogos (D12). Sem eles: sem título, "Cancelar" / "Sim". */
  dialogLabels?: Partial<Record<StudyActionResult, ConfirmationDialogLabels>>;
  /** Chamado depois de excluir, ou de cancelar com `success`. */
  onSuccess?: (kind: StudyActionResult) => void;
  /** Recebe cada mensagem de resultado, para a tela mostrar num aviso único. */
  onFeedback?: (feedback: CustomAlert) => void;
  /**
   * Texto do erro da ação, ou `null` quando o erro já foi tratado (ex.: sessão expirada fez
   * logout). Sem ele, vale a `description` ou a `message` do backend.
   */
  errorMessage?: (error: unknown, kind: StudyActionResult) => string | null;
}

// Erros esperados (404/409/422) ficam na linha: useHandleRequestErrors redirecionaria a página
const errorMessage = (error: unknown) => {
  const { data, description } = error as ResponseError;
  return description || data || 'Não foi possível concluir a ação.';
};

/**
 * Cancelar/Excluir um estudo do Flux (Upload ou Personalizado): confirmação, feedback na linha
 * e refetch tardio após cancelar. Cada tela injeta as próprias mutations e a tag da lista.
 */
export default function useStudyActions(study: StudyActionTarget, {
  triggerDelete,
  deleting,
  triggerCancel,
  cancelling,
  tag,
  dialogLabels = {},
  onSuccess,
  onFeedback,
  errorMessage: customErrorMessage,
}: StudyActionsMutations & StudyActionsOptions) {
  const dispatch = useDispatch();
  const [feedback, setFeedback] = useState<CustomAlert | null>(null);
  const report = (next: CustomAlert) => {
    setFeedback(next);
    onFeedback?.(next);
  };
  const reportError = (error: unknown, kind: StudyActionResult) => {
    const message = customErrorMessage ? customErrorMessage(error, kind) : errorMessage(error);
    if (message !== null) report({ title: '', message, variant: 'danger' });
  };
  const { confirmationDialogBox, setConfirmationDialogBox } = useConfirmationDialogBox();

  const deleteStudy = async () => {
    setFeedback(null);
    try {
      await triggerDelete(study.id).unwrap();
    } catch (error) {
      reportError(error, 'delete');
      return;
    }
    // Fora do try: um erro no callback da tela não vira "falha ao excluir"
    onSuccess?.('delete');
  };

  const cancelStudy = async () => {
    setFeedback(null);
    let response: CancelStudyResponse;
    try {
      response = await triggerCancel(study.id).unwrap();
    } catch (error) {
      reportError(error, 'cancel');
      return;
    }
    report({
      title: '',
      message: response.message,
      variant: response.success ? 'success' : 'warning',
    });
    if (response.success) {
      setTimeout(
        () => dispatch(baseApi.util.invalidateTags([tag])),
        REFETCH_AFTER_CANCEL_MS,
      );
      onSuccess?.('cancel');
    }
  };

  const confirmDelete = () => setConfirmationDialogBox(
    true,
    (
      <span>
        Deseja realmente remover o estudo
        <span className="fw-bold mx-1">{study.ds_nome_estudo}</span>
        ? Esta ação não pode ser desfeita.
      </span>
    ),
    deleteStudy,
    undefined,
    dialogLabels.delete,
  );

  const confirmCancel = () => setConfirmationDialogBox(
    true,
    (
      <span>
        Deseja cancelar o estudo
        <span className="fw-bold mx-1">{study.ds_nome_estudo}</span>
        ? A execução será interrompida.
      </span>
    ),
    cancelStudy,
    undefined,
    dialogLabels.cancel,
  );

  return {
    confirmationDialogBox,
    feedback,
    clearFeedback: () => setFeedback(null),
    confirmDelete,
    confirmCancel,
    deleting,
    cancelling,
  };
}
